import {
  ArrowLeft,
  BarChart3,
  CheckCircle2,
  CircleAlert,
  GitMerge,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  UserRoundCheck,
  Users,
} from "lucide-react";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  createStatementRecipient,
  getStatementRecipient,
  getStatementRecipientReports,
  listStatementRecipients,
  mergeStatementRecipients,
  setDefaultStatementRecipient,
  updateStatementRecipient,
  validateStatementRecipient,
  type StatementRecipientAuditEntry,
  type StatementRecipientCapabilities,
  type StatementRecipientInput,
  type StatementRecipientRecord,
  type StatementRecipientReports,
} from "../../lib/arRecoveryStatementRecipientsApi";

interface ARRecoveryStatementRecipientManagementProps {
  onClose: () => void;
  onOpenPrint?: (statementId: string) => void;
  onOpenSend?: (statementId: string) => void;
  initialClient?: string;
  initialCustomer?: string;
  initialAccount?: string;
  statementContext?: {
    statementId: string;
    customerName: string;
    accountId: string;
  } | null;
}

const EMPTY_FORM: StatementRecipientInput = {
  recipientType: "Primary AR Contact",
  name: "",
  companyName: "",
  email: "",
  phone: "",
  department: "",
  jobTitle: "",
  preferredDeliveryMethod: "Email",
  language: "English",
  timeZone: "America/New_York",
  status: "active",
  optOut: false,
  sourceType: "Manual Override",
  associations: [],
  groupMemberRecipientIds: [],
  reason: "",
};

const DEFAULT_CAPABILITIES: StatementRecipientCapabilities = {
  view: false,
  create: false,
  edit: false,
  deactivate: false,
  setDefault: false,
  validate: false,
  merge: false,
  viewAudit: false,
  viewReports: false,
};

const FIELD_CLASS =
  "focus-ring min-w-0 w-full rounded-xl border border-[#BFEAFF] bg-white px-3 py-2.5 text-sm font-semibold text-[#0A2540]";
const BUTTON_CLASS =
  "focus-ring inline-flex items-center justify-center gap-2 rounded-xl border border-[#BFEAFF] bg-white px-3.5 py-2.5 text-xs font-black uppercase tracking-[0.1em] text-[#0A2540] transition hover:border-[#14B8A6] disabled:cursor-not-allowed disabled:opacity-45";

const toForm = (recipient: StatementRecipientRecord): StatementRecipientInput => ({
  recipientType: recipient.recipientType,
  name: recipient.name,
  companyName: recipient.companyName,
  email: recipient.email,
  phone: recipient.phone,
  department: recipient.department,
  jobTitle: recipient.jobTitle,
  preferredDeliveryMethod: recipient.preferredDeliveryMethod,
  language: recipient.language,
  timeZone: recipient.timeZone,
  status: recipient.status,
  optOut: recipient.optOut,
  sourceType: recipient.sourceType,
  associations: recipient.associations.map((association) => ({
    id: association.id,
    associationType: association.associationType,
    associationId: association.associationId,
    associationName: association.associationName,
    deliveryRole: association.deliveryRole,
    isDefault: association.isDefault,
    isManualOverride: association.isManualOverride,
    priority: association.priority,
    active: association.active,
  })),
  groupMemberRecipientIds: recipient.groupMemberRecipientIds,
  reason: "",
});

const formatDateTime = (value?: string) =>
  value
    ? new Intl.DateTimeFormat("en-US", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value))
    : "Not recorded";

function ValidationPill({ value }: { value?: string | null }) {
  const label = value || "Unvalidated";
  const style =
    label === "Valid"
      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
      : label === "Invalid"
        ? "border-rose-200 bg-rose-50 text-rose-800"
        : label === "Needs Review"
          ? "border-amber-200 bg-amber-50 text-amber-800"
          : "border-slate-200 bg-slate-50 text-slate-700";
  return (
    <span className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.1em] ${style}`}>
      {label}
    </span>
  );
}

function RecipientForm({
  form,
  setForm,
  onSubmit,
  onCancel,
  isSaving,
  title,
  recipientOptions,
}: {
  form: StatementRecipientInput;
  setForm: (form: StatementRecipientInput) => void;
  onSubmit: (event: FormEvent) => void;
  onCancel: () => void;
  isSaving: boolean;
  title: string;
  recipientOptions: StatementRecipientRecord[];
}) {
  const association = form.associations[0] ?? {
    associationType: "Account",
    associationId: "",
    associationName: "",
    deliveryRole: "To",
    isDefault: false,
    isManualOverride: true,
    priority: 100,
    active: true,
  };
  const update = <K extends keyof StatementRecipientInput>(
    key: K,
    value: StatementRecipientInput[K],
  ) => setForm({ ...form, [key]: value });
  const updateAssociation = (key: string, value: string | boolean | number) =>
    update("associations", [{ ...association, [key]: value }]);

  return (
    <form onSubmit={onSubmit} className="grid min-w-0 gap-4" aria-label={title}>
      <div>
        <p className="text-xs font-black uppercase tracking-[0.14em] text-[#14B8A6]">Recipient Editor</p>
        <h2 className="mt-1 font-display text-xl font-bold text-[#0A2540]">{title}</h2>
        <p className="mt-1 text-sm font-semibold text-[#475569]">
          Delivery is not performed here. This workspace governs recipient identity and assignment only.
        </p>
      </div>
      <div className="grid min-w-0 gap-3 md:grid-cols-2 xl:grid-cols-3">
        <label className="grid gap-1 text-xs font-black uppercase tracking-[0.08em] text-[#475569]">
          Recipient type
          <select className={FIELD_CLASS} value={form.recipientType} onChange={(event) => update("recipientType", event.target.value)}>
            {["Primary AR Contact", "Billing Contact", "Accounts Payable Contact", "General Accounting", "Custom Recipient", "Distribution Group"].map((value) => <option key={value}>{value}</option>)}
          </select>
        </label>
        <label className="grid gap-1 text-xs font-black uppercase tracking-[0.08em] text-[#475569]">
          Name
          <input required className={FIELD_CLASS} value={form.name} onChange={(event) => update("name", event.target.value)} />
        </label>
        <label className="grid gap-1 text-xs font-black uppercase tracking-[0.08em] text-[#475569]">
          Company
          <input className={FIELD_CLASS} value={form.companyName} onChange={(event) => update("companyName", event.target.value)} />
        </label>
        <label className="grid gap-1 text-xs font-black uppercase tracking-[0.08em] text-[#475569]">
          Email
          <input type="email" className={FIELD_CLASS} value={form.email} onChange={(event) => update("email", event.target.value)} />
        </label>
        <label className="grid gap-1 text-xs font-black uppercase tracking-[0.08em] text-[#475569]">
          Phone
          <input className={FIELD_CLASS} value={form.phone} onChange={(event) => update("phone", event.target.value)} />
        </label>
        <label className="grid gap-1 text-xs font-black uppercase tracking-[0.08em] text-[#475569]">
          Department
          <input className={FIELD_CLASS} value={form.department} onChange={(event) => update("department", event.target.value)} />
        </label>
        <label className="grid gap-1 text-xs font-black uppercase tracking-[0.08em] text-[#475569]">
          Job title
          <input className={FIELD_CLASS} value={form.jobTitle} onChange={(event) => update("jobTitle", event.target.value)} />
        </label>
        <label className="grid gap-1 text-xs font-black uppercase tracking-[0.08em] text-[#475569]">
          Preferred delivery method
          <select className={FIELD_CLASS} value={form.preferredDeliveryMethod} onChange={(event) => update("preferredDeliveryMethod", event.target.value)}>
            {["Email", "Print", "Portal", "Manual Download", "Future API", "Future EDI"].map((value) => <option key={value}>{value}</option>)}
          </select>
        </label>
        <label className="grid gap-1 text-xs font-black uppercase tracking-[0.08em] text-[#475569]">
          Source
          <select className={FIELD_CLASS} value={form.sourceType} onChange={(event) => update("sourceType", event.target.value)}>
            {["Client Profile", "Customer Profile", "Account", "Placement", "Statement Template", "Manual Override"].map((value) => <option key={value}>{value}</option>)}
          </select>
        </label>
        <label className="grid gap-1 text-xs font-black uppercase tracking-[0.08em] text-[#475569]">
          Language
          <input className={FIELD_CLASS} value={form.language} onChange={(event) => update("language", event.target.value)} />
        </label>
        <label className="grid gap-1 text-xs font-black uppercase tracking-[0.08em] text-[#475569]">
          Time zone
          <input className={FIELD_CLASS} value={form.timeZone} onChange={(event) => update("timeZone", event.target.value)} />
        </label>
        <label className="grid gap-1 text-xs font-black uppercase tracking-[0.08em] text-[#475569]">
          Status
          <select className={FIELD_CLASS} value={form.status} onChange={(event) => update("status", event.target.value)}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </label>
      </div>

      <section className="grid min-w-0 gap-3 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.12em] text-[#0F766E]">Association and delivery role</p>
          <p className="mt-1 text-xs font-semibold text-[#475569]">Manual overrides outrank profile and template defaults during recipient resolution.</p>
        </div>
        <div className="grid min-w-0 gap-3 md:grid-cols-2 xl:grid-cols-4">
          <label className="grid gap-1 text-xs font-black uppercase tracking-[0.08em] text-[#475569]">
            Association type
            <select className={FIELD_CLASS} value={association.associationType} onChange={(event) => updateAssociation("associationType", event.target.value)}>
              {["Client", "Customer", "Account", "Placement", "Statement Template"].map((value) => <option key={value}>{value}</option>)}
            </select>
          </label>
          <label className="grid gap-1 text-xs font-black uppercase tracking-[0.08em] text-[#475569]">
            Record ID
            <input className={FIELD_CLASS} value={association.associationId} onChange={(event) => updateAssociation("associationId", event.target.value)} />
          </label>
          <label className="grid gap-1 text-xs font-black uppercase tracking-[0.08em] text-[#475569]">
            Record name
            <input className={FIELD_CLASS} value={association.associationName} onChange={(event) => updateAssociation("associationName", event.target.value)} />
          </label>
          <label className="grid gap-1 text-xs font-black uppercase tracking-[0.08em] text-[#475569]">
            Delivery role
            <select className={FIELD_CLASS} value={association.deliveryRole} onChange={(event) => updateAssociation("deliveryRole", event.target.value)}>
              {["To", "CC", "BCC"].map((value) => <option key={value}>{value}</option>)}
            </select>
          </label>
        </div>
        <div className="flex flex-wrap gap-4 text-sm font-bold text-[#0A2540]">
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" checked={association.isDefault} onChange={(event) => updateAssociation("isDefault", event.target.checked)} />
            Default for this association
          </label>
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" checked={association.isManualOverride} onChange={(event) => updateAssociation("isManualOverride", event.target.checked)} />
            Manual override
          </label>
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" checked={form.optOut} onChange={(event) => update("optOut", event.target.checked)} />
            Delivery opt-out
          </label>
        </div>
      </section>

      {form.recipientType === "Distribution Group" ? (
        <label className="grid gap-1 text-xs font-black uppercase tracking-[0.08em] text-[#475569]">
          Group members
          <select
            multiple
            className={`${FIELD_CLASS} min-h-32`}
            value={form.groupMemberRecipientIds}
            onChange={(event) =>
              update("groupMemberRecipientIds", Array.from(event.currentTarget.selectedOptions, (option) => option.value))
            }
          >
            {recipientOptions.map((recipient) => <option key={recipient.id} value={recipient.id}>{recipient.name} / {recipient.email || recipient.id}</option>)}
          </select>
        </label>
      ) : null}

      <label className="grid gap-1 text-xs font-black uppercase tracking-[0.08em] text-[#475569]">
        Change reason
        <textarea required className={`${FIELD_CLASS} min-h-24 resize-y`} value={form.reason} onChange={(event) => update("reason", event.target.value)} placeholder="Required audit reason" />
      </label>
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" onClick={onCancel} className={BUTTON_CLASS}>Cancel</button>
        <button type="submit" disabled={isSaving} className={`${BUTTON_CLASS} border-[#14B8A6] bg-[#14B8A6] text-white`}>
          {isSaving ? "Saving" : "Save Recipient"}
        </button>
      </div>
    </form>
  );
}

export function ARRecoveryStatementRecipientManagement({
  onClose,
  onOpenPrint,
  onOpenSend,
  initialClient = "",
  initialCustomer = "",
  initialAccount = "",
  statementContext,
}: ARRecoveryStatementRecipientManagementProps) {
  const [recipients, setRecipients] = useState<StatementRecipientRecord[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [selected, setSelected] = useState<StatementRecipientRecord | null>(null);
  const [audit, setAudit] = useState<StatementRecipientAuditEntry[]>([]);
  const [capabilities, setCapabilities] = useState(DEFAULT_CAPABILITIES);
  const [summary, setSummary] = useState({ total: 0, active: 0, inactive: 0, invalid: 0, needsReview: 0, duplicates: 0 });
  const [pagination, setPagination] = useState({ page: 1, pageSize: 25, total: 0, totalPages: 1 });
  const [search, setSearch] = useState(initialAccount || statementContext?.accountId || "");
  const [client, setClient] = useState(initialClient);
  const [customer, setCustomer] = useState(
    initialAccount || statementContext ? "" : initialCustomer,
  );
  const [status, setStatus] = useState("");
  const [method, setMethod] = useState("");
  const [validation, setValidation] = useState("");
  const [updatedFrom, setUpdatedFrom] = useState("");
  const [updatedTo, setUpdatedTo] = useState("");
  const [page, setPage] = useState(1);
  const [mode, setMode] = useState<"queue" | "create" | "edit" | "reports">("queue");
  const [form, setForm] = useState<StatementRecipientInput>(EMPTY_FORM);
  const [reports, setReports] = useState<StatementRecipientReports | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError("");
    listStatementRecipients({ search, client, customer, status, method, validation, updatedFrom, updatedTo, page, pageSize: 25 })
      .then((payload) => {
        if (cancelled) return;
        setRecipients(payload.recipients);
        setSummary(payload.summary);
        setPagination(payload.pagination);
        setCapabilities(payload.capabilities);
        setSelectedId((current) =>
          current && payload.recipients.some((recipient) => recipient.id === current)
            ? current
            : payload.recipients[0]?.id ?? "",
        );
      })
      .catch((nextError) => {
        if (!cancelled) setError(nextError instanceof Error ? nextError.message : "Recipients could not be loaded.");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [search, client, customer, status, method, validation, updatedFrom, updatedTo, page, reloadKey]);

  useEffect(() => {
    if (!selectedId) {
      setSelected(null);
      setAudit([]);
      return;
    }
    let cancelled = false;
    getStatementRecipient(selectedId)
      .then((payload) => {
        if (cancelled) return;
        setSelected(payload.recipient);
        setAudit(payload.audit);
      })
      .catch((nextError) => {
        if (!cancelled) setError(nextError instanceof Error ? nextError.message : "Recipient details could not be loaded.");
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId, reloadKey]);

  const duplicateTarget = useMemo(
    () => recipients.find((recipient) => recipient.id === selected?.latestValidation?.duplicateRecipientId) ?? null,
    [recipients, selected],
  );

  const refresh = () => setReloadKey((value) => value + 1);
  const clearNotices = () => {
    setMessage("");
    setError("");
  };

  const startCreate = () => {
    clearNotices();
    setForm({
      ...EMPTY_FORM,
      companyName: statementContext?.customerName ?? "",
      associations: statementContext
        ? [{
            associationType: "Account",
            associationId: initialAccount || statementContext.accountId,
            associationName: initialCustomer || statementContext.customerName,
            deliveryRole: "To",
            isDefault: false,
            isManualOverride: true,
            priority: 100,
            active: true,
          }]
        : initialAccount
          ? [{
              associationType: "Account",
              associationId: initialAccount,
              associationName: initialCustomer || initialAccount,
              deliveryRole: "To",
              isDefault: false,
              isManualOverride: true,
              priority: 100,
              active: true,
            }]
          : [],
    });
    setMode("create");
  };

  const saveRecipient = async (event: FormEvent) => {
    event.preventDefault();
    clearNotices();
    setIsSaving(true);
    try {
      const payload =
        mode === "edit" && selected
          ? await updateStatementRecipient(selected.id, form)
          : await createStatementRecipient(form);
      setSelectedId(payload.recipient.id);
      setMessage(mode === "edit" ? "Recipient updated and audited." : "Recipient created and audited.");
      setMode("queue");
      refresh();
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : "Recipient could not be saved.");
    } finally {
      setIsSaving(false);
    }
  };

  const runValidation = async () => {
    if (!selected) return;
    clearNotices();
    try {
      const result = await validateStatementRecipient(selected.id);
      setMessage(`Validation completed: ${result.status}.`);
      refresh();
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : "Validation failed.");
    }
  };

  const deactivate = async () => {
    if (!selected || !capabilities.deactivate) return;
    clearNotices();
    try {
      await updateStatementRecipient(selected.id, {
        ...toForm(selected),
        status: "inactive",
        reason: "Recipient deactivated from Recipient Management.",
      });
      setMessage("Recipient deactivated. Historical assignments remain auditable.");
      refresh();
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : "Recipient could not be deactivated.");
    }
  };

  const setDefault = async (associationId: string) => {
    if (!selected) return;
    clearNotices();
    try {
      await setDefaultStatementRecipient(
        selected.id,
        associationId,
        "Default recipient assignment changed from Recipient Management.",
      );
      setMessage("Default recipient assignment updated.");
      refresh();
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : "Default assignment could not be updated.");
    }
  };

  const mergeDuplicate = async () => {
    if (!selected || !duplicateTarget) return;
    clearNotices();
    try {
      const payload = await mergeStatementRecipients(
        selected.id,
        duplicateTarget.id,
        "Validated duplicate recipient merged into the retained canonical recipient.",
      );
      setSelectedId(payload.recipient.id);
      setMessage("Duplicate merged. The source recipient was deactivated and retained in audit history.");
      refresh();
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : "Duplicate could not be merged.");
    }
  };

  const loadReports = async () => {
    clearNotices();
    try {
      setReports(await getStatementRecipientReports());
      setMode("reports");
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : "Recipient reports could not be loaded.");
    }
  };

  if (mode === "create" || mode === "edit") {
    return (
      <div className="grid min-w-0 max-w-full gap-4 overflow-hidden rounded-[1.35rem] border border-[#BFEAFF] bg-white p-4 shadow-[0_12px_30px_rgba(10,37,64,.07)]">
        <RecipientForm
          form={form}
          setForm={setForm}
          onSubmit={saveRecipient}
          onCancel={() => setMode("queue")}
          isSaving={isSaving}
          title={mode === "edit" ? `Edit ${selected?.name ?? "Recipient"}` : "Create Recipient"}
          recipientOptions={recipients.filter((recipient) => recipient.id !== selected?.id && recipient.status === "active")}
        />
      </div>
    );
  }

  if (mode === "reports") {
    return (
      <div className="grid min-w-0 max-w-full gap-4 overflow-hidden">
        <button type="button" onClick={() => setMode("queue")} className={`${BUTTON_CLASS} w-fit`}><ArrowLeft className="size-4" /> Back to Recipient Queue</button>
        <section className="rounded-[1.35rem] border border-[#BFEAFF] bg-white p-4 shadow-[0_12px_30px_rgba(10,37,64,.07)]">
          <p className="text-xs font-black uppercase tracking-[0.14em] text-[#14B8A6]">Recipient Reporting</p>
          <h2 className="mt-1 font-display text-2xl font-bold text-[#0A2540]">Operational recipient quality</h2>
          <p className="mt-1 text-sm font-semibold text-[#475569]">Generated {formatDateTime(reports?.generatedAt)}</p>
          <div className="mt-4 grid gap-3 lg:grid-cols-2">
            <article className="rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
              <h3 className="font-display text-lg font-bold">Recipients by client</h3>
              <div className="mt-3 grid gap-2">{reports?.byClient.length ? reports.byClient.map((item) => <div key={item.client} className="flex justify-between gap-3 text-sm font-semibold"><span>{item.client}</span><strong>{item.count}</strong></div>) : <p className="text-sm text-[#475569]">No client associations.</p>}</div>
            </article>
            <article className="rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
              <h3 className="font-display text-lg font-bold">Recipients by delivery method</h3>
              <div className="mt-3 grid gap-2">{reports?.byMethod.map((item) => <div key={item.method} className="flex justify-between gap-3 text-sm font-semibold"><span>{item.method}</span><strong>{item.count}</strong></div>)}</div>
            </article>
          </div>
          <div className="mt-3 grid gap-3 md:grid-cols-3">
            {[["Invalid", reports?.invalid.length ?? 0], ["Duplicate", reports?.duplicates.length ?? 0], ["Inactive", reports?.inactive.length ?? 0]].map(([label, value]) => <article key={String(label)} className="rounded-2xl border border-[#BFEAFF] bg-white p-4"><p className="text-xs font-black uppercase tracking-[0.12em] text-[#0F766E]">{label}</p><p className="mt-1 font-display text-2xl font-bold">{value}</p></article>)}
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="grid min-w-0 max-w-full gap-3 overflow-hidden text-[#0A2540]" data-statement-recipient-management="operational">
      <section className="min-w-0 rounded-[1.35rem] border border-[#BFEAFF] bg-white p-4 shadow-[0_12px_30px_rgba(10,37,64,.07)]">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-black uppercase tracking-[0.16em] text-[#14B8A6]">Statement Center</p>
            <h1 className="mt-1 break-words font-display text-[1.65rem] font-bold leading-tight">Recipient Management</h1>
            <p className="mt-1 max-w-4xl break-words text-sm font-semibold leading-5 text-[#475569]">
              Govern statement recipients, delivery roles, defaults, validation, associations, and audit history. No statement delivery occurs in this workspace.
            </p>
            {statementContext ? <p className="mt-2 text-xs font-black uppercase tracking-[0.1em] text-[#0F766E]">Context: {statementContext.customerName} / {statementContext.accountId} / {statementContext.statementId}</p> : null}
          </div>
          <div className="flex flex-wrap gap-2">
            {onOpenSend && statementContext ? <button type="button" onClick={() => onOpenSend(statementContext.statementId)} className={`${BUTTON_CLASS} border-[#14B8A6] bg-[#14B8A6] text-white`}>Send Now</button> : null}
            {onOpenPrint && statementContext ? <button type="button" onClick={() => onOpenPrint(statementContext.statementId)} className={BUTTON_CLASS}>Open Statement Print</button> : null}
            <button type="button" onClick={onClose} className={BUTTON_CLASS}><ArrowLeft className="size-4" /> Statement Center</button>
            {capabilities.viewReports ? <button type="button" onClick={loadReports} className={BUTTON_CLASS}><BarChart3 className="size-4" /> Reports</button> : null}
            {capabilities.create ? <button type="button" onClick={startCreate} className={`${BUTTON_CLASS} border-[#14B8A6] bg-[#14B8A6] text-white`}><Plus className="size-4" /> Create Recipient</button> : null}
          </div>
        </div>
      </section>

      <section className="grid min-w-0 gap-2.5 sm:grid-cols-2 xl:grid-cols-6">
        {[
          { label: "Total", value: summary.total, icon: Users },
          { label: "Active", value: summary.active, icon: UserRoundCheck },
          { label: "Inactive", value: summary.inactive, icon: CircleAlert },
          { label: "Invalid", value: summary.invalid, icon: CircleAlert },
          { label: "Needs review", value: summary.needsReview, icon: ShieldCheck },
          { label: "Duplicates", value: summary.duplicates, icon: GitMerge },
        ].map((card) => {
          const Icon = card.icon;
          return (
          <article key={card.label} className="min-w-0 rounded-[1.1rem] border border-[#BFEAFF] bg-white p-3 shadow-[0_8px_20px_rgba(10,37,64,.045)]">
            <div className="flex items-center justify-between gap-2"><p className="text-[10px] font-black uppercase tracking-[0.12em] text-[#0F766E]">{card.label}</p><Icon className="size-4 text-[#14B8A6]" aria-hidden="true" /></div>
            <p className="mt-1 font-display text-2xl font-bold">{card.value}</p>
          </article>
          );
        })}
      </section>

      {message ? <div role="status" className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-800">{message}</div> : null}
      {error ? <div role="alert" className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-800">{error}</div> : null}

      <section className="grid min-w-0 gap-3 rounded-[1.35rem] border border-[#BFEAFF] bg-white p-3.5 shadow-[0_12px_30px_rgba(10,37,64,.07)]">
        <div className="grid min-w-0 gap-2 lg:grid-cols-[minmax(15rem,2fr)_repeat(5,minmax(8rem,1fr))_auto]">
          <label className="relative min-w-0">
            <span className="sr-only">Search recipients</span>
            <Search className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-[#14B8A6]" />
            <input className={`${FIELD_CLASS} pl-10`} value={search} onChange={(event) => { setSearch(event.target.value); setPage(1); }} placeholder="Search name, email, phone, association" />
          </label>
          <input className={FIELD_CLASS} value={client} onChange={(event) => { setClient(event.target.value); setPage(1); }} placeholder="Client" aria-label="Filter recipients by client" />
          <input className={FIELD_CLASS} value={customer} onChange={(event) => { setCustomer(event.target.value); setPage(1); }} placeholder="Customer" aria-label="Filter recipients by customer" />
          <select className={FIELD_CLASS} value={status} onChange={(event) => { setStatus(event.target.value); setPage(1); }} aria-label="Filter recipients by status"><option value="">All statuses</option><option value="active">Active</option><option value="inactive">Inactive</option></select>
          <select className={FIELD_CLASS} value={method} onChange={(event) => { setMethod(event.target.value); setPage(1); }} aria-label="Filter recipients by delivery method"><option value="">All methods</option>{["Email", "Print", "Portal", "Manual Download", "Future API", "Future EDI"].map((value) => <option key={value}>{value}</option>)}</select>
          <select className={FIELD_CLASS} value={validation} onChange={(event) => { setValidation(event.target.value); setPage(1); }} aria-label="Filter recipients by validation"><option value="">All validation</option>{["Valid", "Invalid", "Needs Review", "Unvalidated"].map((value) => <option key={value}>{value}</option>)}</select>
          <button type="button" onClick={refresh} className={BUTTON_CLASS}><RefreshCw className="size-4" /> Refresh</button>
        </div>
        <div className="grid min-w-0 gap-2 sm:grid-cols-2 lg:max-w-xl">
          <label className="grid gap-1 text-[10px] font-black uppercase tracking-[0.08em] text-[#475569]">Updated from<input type="date" className={FIELD_CLASS} value={updatedFrom} onChange={(event) => setUpdatedFrom(event.target.value)} /></label>
          <label className="grid gap-1 text-[10px] font-black uppercase tracking-[0.08em] text-[#475569]">Updated to<input type="date" className={FIELD_CLASS} value={updatedTo} onChange={(event) => setUpdatedTo(event.target.value)} /></label>
        </div>

        <div className="min-w-0 max-w-full overflow-x-auto overscroll-x-contain rounded-[1.1rem] border border-[#BFEAFF]">
          <table className="w-full min-w-[74rem] border-collapse text-left text-sm">
            <thead className="bg-[#F4FBFF] text-[10px] font-black uppercase tracking-[0.1em]">
              <tr>{["Client", "Customer", "Recipient", "Email", "Method", "Default", "Status", "Updated", "Validation"].map((heading) => <th key={heading} className="border-b border-[#BFEAFF] px-3 py-3">{heading}</th>)}</tr>
            </thead>
            <tbody>
              {recipients.map((recipient) => {
                const clientAssociation = recipient.associations.find((association) => association.associationType === "Client");
                const customerAssociation = recipient.associations.find((association) => association.associationType === "Customer");
                const active = recipient.id === selectedId;
                return (
                  <tr key={recipient.id} tabIndex={0} role="button" aria-pressed={active} onClick={() => setSelectedId(recipient.id)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); setSelectedId(recipient.id); } }} className={`cursor-pointer border-b border-[#E0F2FE] ${active ? "bg-[#ECFEFF]" : "bg-white hover:bg-[#F4FBFF]"}`}>
                    <td className="px-3 py-3 font-semibold">{clientAssociation?.associationName || "-"}</td>
                    <td className="px-3 py-3 font-semibold">{customerAssociation?.associationName || recipient.companyName || "-"}</td>
                    <td className="px-3 py-3"><strong className="block">{recipient.name}</strong><span className="text-xs text-[#475569]">{recipient.recipientType}</span></td>
                    <td className="max-w-64 break-all px-3 py-3 font-semibold">{recipient.email || "-"}</td>
                    <td className="px-3 py-3 font-semibold">{recipient.preferredDeliveryMethod}</td>
                    <td className="px-3 py-3 font-semibold">{recipient.associations.some((association) => association.isDefault) ? "Yes" : "No"}</td>
                    <td className="px-3 py-3 font-semibold capitalize">{recipient.status}</td>
                    <td className="px-3 py-3 font-semibold">{formatDateTime(recipient.updatedAt)}</td>
                    <td className="px-3 py-3"><ValidationPill value={recipient.latestValidation?.status} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!recipients.length ? <p className="p-6 text-sm font-semibold text-[#475569]">{isLoading ? "Loading recipients..." : "No recipients matched the current filters."}</p> : null}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 text-xs font-black uppercase tracking-[0.1em] text-[#475569]">
          <span>Page {pagination.page} of {pagination.totalPages} / {pagination.total} recipients</span>
          <div className="flex gap-2"><button type="button" className={BUTTON_CLASS} disabled={page <= 1} onClick={() => setPage((value) => Math.max(1, value - 1))}>Previous</button><button type="button" className={BUTTON_CLASS} disabled={page >= pagination.totalPages} onClick={() => setPage((value) => value + 1)}>Next</button></div>
        </div>
      </section>

      {selected ? (
        <section className="grid min-w-0 gap-4 rounded-[1.35rem] border border-[#BFEAFF] bg-white p-4 shadow-[0_12px_30px_rgba(10,37,64,.07)]">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs font-black uppercase tracking-[0.14em] text-[#0F766E]">Recipient Detail</p>
              <h2 className="mt-1 break-words font-display text-2xl font-bold">{selected.name}</h2>
              <p className="mt-1 break-all text-sm font-semibold text-[#475569]">{selected.email || "No email"} / {selected.recipientType}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {capabilities.validate ? <button type="button" onClick={runValidation} className={BUTTON_CLASS}><CheckCircle2 className="size-4" /> Validate</button> : null}
              {capabilities.edit ? <button type="button" onClick={() => { setForm(toForm(selected)); setMode("edit"); }} className={BUTTON_CLASS}>Edit</button> : null}
              {capabilities.deactivate && selected.status === "active" ? <button type="button" onClick={deactivate} className={BUTTON_CLASS}>Deactivate</button> : null}
              {capabilities.merge && duplicateTarget ? <button type="button" onClick={mergeDuplicate} className={BUTTON_CLASS}><GitMerge className="size-4" /> Merge Duplicate</button> : null}
            </div>
          </div>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            {[["Company", selected.companyName || "-"], ["Phone", selected.phone || "-"], ["Department", selected.department || "-"], ["Job title", selected.jobTitle || "-"], ["Method", selected.preferredDeliveryMethod], ["Language", selected.language], ["Time zone", selected.timeZone], ["Source", selected.sourceType]].map(([label, value]) => <article key={label} className="min-w-0 rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] p-3"><p className="text-[10px] font-black uppercase tracking-[0.1em] text-[#0F766E]">{label}</p><p className="mt-1 break-words text-sm font-bold">{value}</p></article>)}
          </div>
          <div className="grid gap-3 lg:grid-cols-2">
            <article className="rounded-2xl border border-[#BFEAFF] p-4">
              <h3 className="font-display text-lg font-bold">Associations and assignments</h3>
              <div className="mt-3 grid gap-2">
                {selected.associations.length ? selected.associations.map((association) => <div key={association.id} className="rounded-xl bg-[#F4FBFF] p-3"><div className="flex flex-wrap items-center justify-between gap-2"><div><strong className="block text-sm">{association.associationType}: {association.associationName}</strong><span className="text-xs font-semibold text-[#475569]">{association.associationId} / {association.deliveryRole} / {association.isManualOverride ? "Manual override" : "Inherited"}</span></div>{capabilities.setDefault && !association.isDefault ? <button type="button" className={BUTTON_CLASS} onClick={() => setDefault(association.id)}>Set Default</button> : <span className="text-xs font-black uppercase text-[#0F766E]">{association.isDefault ? "Default" : ""}</span>}</div></div>) : <p className="text-sm font-semibold text-[#475569]">No associations are assigned.</p>}
              </div>
            </article>
            <article className="rounded-2xl border border-[#BFEAFF] p-4">
              <h3 className="font-display text-lg font-bold">Validation and preferences</h3>
              <div className="mt-3 flex flex-wrap items-center gap-2"><ValidationPill value={selected.latestValidation?.status} />{selected.optOut ? <span className="rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-[10px] font-black uppercase text-rose-800">Opted out</span> : null}</div>
              <ul className="mt-3 grid gap-2 text-sm font-semibold text-[#475569]">{selected.latestValidation?.issues.length ? selected.latestValidation.issues.map((issue) => <li key={issue} className="flex gap-2"><CircleAlert className="mt-0.5 size-4 shrink-0 text-amber-600" />{issue}</li>) : <li>No validation issues recorded.</li>}</ul>
              {selected.recipientType === "Distribution Group" ? <p className="mt-3 text-sm font-semibold">Group members: {selected.groupMemberRecipientIds.length}</p> : null}
            </article>
          </div>
          {capabilities.viewAudit ? (
            <article className="rounded-2xl border border-[#BFEAFF] p-4">
              <h3 className="font-display text-lg font-bold">Recipient history and audit</h3>
              <div className="mt-3 grid gap-2">
                {audit.length ? audit.map((entry) => <div key={entry.id} className="grid gap-1 rounded-xl bg-[#F4FBFF] p-3 md:grid-cols-[minmax(10rem,1fr)_minmax(12rem,2fr)_auto]"><strong className="text-sm capitalize">{entry.action.replaceAll("_", " ")}</strong><span className="break-words text-sm font-semibold text-[#475569]">{entry.reason}</span><span className="text-xs font-semibold text-[#475569]">{entry.actorName} / {formatDateTime(entry.occurredAt)}</span></div>) : <p className="text-sm font-semibold text-[#475569]">No recipient history has been recorded.</p>}
              </div>
            </article>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
